/**
 * The grain layer — a tiling square of monochrome film noise, generated rather
 * than shipped as an asset so it costs no request and no binary in the repo.
 *
 * Built with `createElementNS` and serialised, NOT written as a template
 * string or JSX. `feTurbulence` reads `baseFrequency` and `numOctaves`, and
 * both are case-sensitive: anything that routes the markup through the HTML
 * parser (`innerHTML`, a JSX host element, `createElement` without the
 * namespace) lowercases attribute names, and `basefrequency` is not an
 * attribute `feTurbulence` has ever heard of. It is ignored, the primitive
 * falls back to its default of 0, and the result is a flat field — no error,
 * no warning, no grain. The namespace is what keeps the spelling.
 */

import { SVG, toSvgDataUri } from './svgDataUri'

export function buildGrainDataUri(tile = 170): string {
  const svg = document.createElementNS(SVG, 'svg')
  // No setAttribute('xmlns', SVG) here, though the plan wrote one. The element
  // is already IN that namespace, so the serializer emits the declaration
  // itself; setting the literal attribute as well creates a second, no-namespace
  // attribute that happens to be spelled "xmlns", and the output carries it
  // twice. Duplicate attributes are not well-formed XML, and image/svg+xml is
  // parsed as XML — so the tile that results is a parse error rather than a
  // picture, which is total absence of grain rather than degraded grain.
  svg.setAttribute('width', String(tile))
  svg.setAttribute('height', String(tile))

  const filter = document.createElementNS(SVG, 'filter')
  // Fixed, not uniquified. Each data URI is parsed as its own document, so two
  // grain layers on one page cannot collide — and a random suffix would make
  // the URI differ between calls, which defeats the memo in AppTexture and
  // re-decodes the image on every render.
  filter.setAttribute('id', 'g')

  const turb = document.createElementNS(SVG, 'feTurbulence')
  turb.setAttribute('type', 'fractalNoise')
  turb.setAttribute('baseFrequency', '0.8')
  turb.setAttribute('numOctaves', '4')
  // Without stitchTiles the noise is generated in the filter region's own
  // coordinate space and the seams between repeats are visible as a grid.
  turb.setAttribute('stitchTiles', 'stitch')

  // feTurbulence emits per-channel noise, which is coloured static. Grain is
  // monochrome, and this is what makes it so — after the turbulence, never
  // before it.
  const sat = document.createElementNS(SVG, 'feColorMatrix')
  sat.setAttribute('type', 'saturate')
  sat.setAttribute('values', '0')

  filter.append(turb, sat)

  const rect = document.createElementNS(SVG, 'rect')
  rect.setAttribute('width', '100%')
  rect.setAttribute('height', '100%')
  rect.setAttribute('filter', 'url(#g)')

  svg.append(filter, rect)

  return toSvgDataUri(svg)
}
